<template>
  <div class="page-block orders-page">
    <h2 class="orders-title">我的订单</h2>
    <p v-if="!isLogin" class="orders-hint">请先登录后查看支付订单（积分充值 / 会员购买）。</p>
    <template v-else>
      <div class="tab-row">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          type="button"
          class="tab-btn"
          :class="{ active: filterType === tab.value }"
          @click="onTab(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>
      <div v-if="loadError" class="orders-error">{{ loadError }}</div>
      <div v-else-if="loading && !orders.length" class="orders-empty">加载中…</div>
      <div v-else-if="!orders.length" class="orders-empty">暂无订单</div>
      <div v-else class="table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th>订单号</th>
              <th>类型</th>
              <th>说明</th>
              <th>实付</th>
              <th>积分/权益</th>
              <th>支付方式</th>
              <th>状态</th>
              <th>时间</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in orders" :key="item.id">
              <td class="mono">{{ item.id }}</td>
              <td>{{ kindLabel(item.orderKind) }}</td>
              <td class="scene-cell">{{ item.scene }}</td>
              <td>{{ item.amount }}</td>
              <td>{{ pointsCell(item) }}</td>
              <td>{{ item.payType }}</td>
              <td>
                <span class="status-pill" :class="statusClass(item.statusCode)">{{ item.status }}</span>
              </td>
              <td class="time-cell">{{ item.time }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="orders.length && hasMore" class="load-more-row">
        <button type="button" class="load-more-btn" :disabled="loadingMore" @click="loadMore">
          {{ loadingMore ? "加载中…" : "加载更多" }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { userApi } from "../api/user";

export default {
  name: "OrdersPage",
  data() {
    return {
      tabs: [
        { label: "全部", value: "all" },
        { label: "积分充值", value: "recharge" },
        { label: "会员购买", value: "vip" }
      ],
      filterType: "all",
      orders: [],
      offset: 0,
      hasMore: false,
      loading: false,
      loadingMore: false,
      loadError: ""
    };
  },
  computed: {
    isLogin() {
      return !!localStorage.getItem("user_token");
    }
  },
  watch: {
    filterType() {
      this.resetAndFetch();
    }
  },
  created() {
    if (this.isLogin) {
      this.resetAndFetch();
    }
  },
  methods: {
    kindLabel(kind) {
      if (kind === "vip") return "会员";
      return "充值";
    },
    pointsCell(item) {
      const n = Number(item.points);
      if (!Number.isFinite(n) || n <= 0) return "—";
      if (item.orderKind === "vip") return `含积分 ${n}`;
      return `+${n}`;
    },
    statusClass(code) {
      const c = String(code || "");
      if (c === "paid") return "ok";
      if (c === "pending") return "pending";
      if (c === "failed" || c === "closed") return "bad";
      return "";
    },
    normalizePayload(data) {
      if (Array.isArray(data)) {
        return { list: data, hasMore: false, nextOffset: data.length };
      }
      const list = data && Array.isArray(data.list) ? data.list : [];
      return {
        list,
        hasMore: !!(data && data.hasMore),
        nextOffset: data && data.nextOffset != null ? data.nextOffset : list.length
      };
    },
    async resetAndFetch() {
      if (!this.isLogin) return;
      this.offset = 0;
      this.orders = [];
      this.hasMore = false;
      this.loadError = "";
      this.loading = true;
      try {
        const raw = await userApi.orders({
          offset: 0,
          limit: 20,
          type: this.filterType
        });
        const { list, hasMore, nextOffset } = this.normalizePayload(raw);
        this.orders = list;
        this.hasMore = hasMore;
        this.offset = nextOffset;
      } catch (e) {
        this.loadError = (e && e.message) || "加载失败";
      } finally {
        this.loading = false;
      }
    },
    onTab(value) {
      if (this.filterType === value) return;
      this.filterType = value;
    },
    async loadMore() {
      if (!this.hasMore || this.loadingMore || this.loading) return;
      this.loadingMore = true;
      try {
        const raw = await userApi.orders({
          offset: this.offset,
          limit: 20,
          type: this.filterType
        });
        const { list, hasMore, nextOffset } = this.normalizePayload(raw);
        const seen = new Set(this.orders.map(o => o.id));
        list.forEach(row => {
          if (row && row.id && !seen.has(row.id)) {
            seen.add(row.id);
            this.orders.push(row);
          }
        });
        this.hasMore = hasMore;
        this.offset = nextOffset;
      } catch (e) {
        window.alert((e && e.message) || "加载更多失败");
      } finally {
        this.loadingMore = false;
      }
    }
  }
};
</script>

<style scoped>
.orders-page {
  color: #e8e8e8;
  min-width: 0;
  max-width: 100%;
}

.orders-title {
  margin: 0 0 16px;
  font-size: 20px;
  font-weight: 600;
}

.orders-hint,
.orders-empty,
.orders-error {
  color: #9d9d9d;
  font-size: 14px;
  margin: 12px 0;
}

.orders-error {
  color: #e57373;
}

.tab-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.tab-btn {
  border: 1px solid #444;
  background: #2a2a2a;
  color: #ccc;
  border-radius: 8px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 13px;
}

.tab-btn:hover {
  border-color: #666;
  color: #fff;
}

.tab-btn.active {
  border-color: #7c6cf9;
  background: #333;
  color: #fff;
}

.table-wrap {
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid #3a3a3a;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #252525;
}

.table th,
.table td {
  padding: 10px 12px;
  text-align: center;
  border-bottom: 1px solid #3a3a3a;
}

.table th {
  background: #1e1e1e;
  color: #bdbdbd;
  font-weight: 500;
  white-space: nowrap;
}

.table tbody tr:hover {
  background: #2c2c2c;
}

.mono {
  font-family: ui-monospace, monospace;
  font-size: 14px;
  word-break: break-all;
  max-width: 160px;
}

.scene-cell {
  max-width: 220px;
  color: #cfcfcf;
}

.time-cell {
  white-space: nowrap;
  color: #aaa;
}

.status-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 14px;
  background: #3a3a3a;
}

.status-pill.ok {
  background: rgba(76, 175, 80, 0.2);
  color: #a5d6a7;
}

.status-pill.pending {
  background: rgba(255, 193, 7, 0.15);
  color: #ffe082;
}

.status-pill.bad {
  background: rgba(244, 67, 54, 0.15);
  color: #ef9a9a;
}

.load-more-row {
  margin-top: 16px;
  text-align: center;
}

.load-more-btn {
  min-width: 120px;
  padding: 8px 18px;
  border-radius: 8px;
  border: 1px solid #555;
  background: #2f2f2f;
  color: #eee;
  cursor: pointer;
}

.load-more-btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.load-more-btn:not(:disabled):hover {
  border-color: #7c6cf9;
}

@media (max-width: 768px) {
  .table-wrap {
    max-width: 100%;
    -webkit-overflow-scrolling: touch;
  }

  .table {
    font-size: 14px;
  }

  .table th,
  .table td {
    padding: 8px 6px;
    min-width:160px;
  }

  .mono {
    max-width: none;
  }

  .scene-cell {
    max-width: 140px;
    word-break: break-word;
  }

  .time-cell {
    white-space: normal;
    word-break: break-all;
  }
}
</style>
